// react
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Auth } from 'aws-amplify';

// material ui imports
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import Button from '@material-ui/core/Button';
import Snackbar from '@mui/material/Snackbar';

import IndicationForm from '../IndicationForm';
import SBASForm from '../SBASForm';
import ComparePdfsModal from '../ComparePdfsModal';
import CompareVersionsModal from '../CompareVersionsModal';

// Context
import GlobalStore from '../../../store';
import Actions from '../../../store/actions';
import AdminStore from '../../../store/AdminPage';
import AdminActions from '../../../store/AdminPage/actions';

// api
import getIndications from '../../../api/submission_details';

import { HttpResponse } from '../constant';
import useLabel from '../hooks/useLabel';
import useComparePdfs from '../hooks/useComparePdfs';
import useCompareVersions from '../hooks/useCompareVersions';
import useIndicationAPIs from '../hooks/useIndicationAPIs';
import type { ComparePdfViewMode, SourceSubmission } from '../types';
import styles from './styles';

const getSourceSubmissionKey = (sourceSubmission: SourceSubmission | null): string => {
  if (
    !sourceSubmission?.applno ||
    !sourceSubmission.submission_type ||
    sourceSubmission.submission_number == null
  ) {
    return '';
  }

  return `${sourceSubmission.applno}-${sourceSubmission.submission_type}-${sourceSubmission.submission_number}`;
};

const getSourceSubmissionUrl = (sourceSubmission: SourceSubmission | null): string | null => {
  const sourceKey = getSourceSubmissionKey(sourceSubmission);
  const documentId = sourceSubmission?.document_ids?.[0];

  if (!sourceSubmission?.data_source || !sourceKey || documentId == null) {
    return null;
  }

  return `/snapshot/${encodeURIComponent(sourceSubmission.data_source)}/${encodeURIComponent(
    sourceKey
  )}/${documentId}`;
};

const SnapshotSummary = ({ match }: any) => {
  const history = useHistory();

  const { adminDispatch, adminState } = useContext<any>(AdminStore);
  const { dispatch } = useContext<any>(GlobalStore);
  const appKey = match?.params?.key;
  const center = match?.params?.center;
  const documentId = match?.params?.documentId;
  const submissionProps = appKey?.split('-');
  const [previousSubmission, setPreviousSubmission] = useState<any>(null);
  const [sourceSubmission, setSourceSubmission] = useState<SourceSubmission | null>(null);
  const [usePrevSubmissionData, setUsePrevSubmissionData] = useState<boolean>(false);
  const [user, setUser] = useState<{ curator_name: string; curator_email: string } | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; isError?: boolean }>({
    open: false,
    message: ''
  });
  const [pdfViewMode, setPdfViewMode] = useState<ComparePdfViewMode>('clinical_studies');

  const applno = submissionProps?.[0]?.trim() ?? '';
  const submissionType = submissionProps?.[1]?.trim() ?? '';
  const submissionNumber = submissionProps?.[2]?.trim() ?? '';
  const hasSourceSubmission = Boolean(sourceSubmission);
  const sourceSubmissionKey = getSourceSubmissionKey(sourceSubmission);
  const sourceSubmissionUrl = getSourceSubmissionUrl(sourceSubmission);
  const changeDashboardUrl = `/snapshot-comparison/${encodeURIComponent(applno)}`;
  const sourceDocumentIds = sourceSubmission?.document_ids?.join(', ') || 'N/A';

  const { pdfUrl, loading, errMessage, getLabelPdf } = useLabel();
  const comparePdfs = useComparePdfs({
    applno,
    submissionType,
    submissionNumber,
    center,
    documentId,
    section: pdfViewMode === 'full_label' ? undefined : pdfViewMode
  });
  const onShowSnackbar = useCallback((message: string, isError?: boolean) => {
    setSnackbar({ open: true, message, isError });
  }, []);

  const refreshCurrentIndications = useCallback(async () => {
    try {
      const response = await getIndications(
        applno,
        submissionType,
        submissionNumber,
        center,
        documentId
      );
      if (response?.data?.Status === HttpResponse.OK) {
        const { indications = [] } = response.data.Success ?? {};
        adminDispatch({
          type: AdminActions.SET_SUBMISSION_INDICATIONS,
          value: indications
        });
      }
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message ?? 'Failed to refresh', isError: true });
    }
  }, [applno, submissionType, submissionNumber, center, documentId, adminDispatch]);

  const compareVersions = useCompareVersions({
    applno,
    submissionType,
    submissionNumber,
    center,
    documentId,
    user,
    currentIndications: adminState?.indications ?? [],
    refreshCurrentIndications,
    onShowSnackbar,
    readOnly: hasSourceSubmission
  });

  const apiIndications = useMemo(() => {
    if (usePrevSubmissionData) return adminState.indications;
    if (previousSubmission) return [];
    return adminState.indications ?? [];
  }, [usePrevSubmissionData, previousSubmission, adminState.indications]);

  const indicationApi = useIndicationAPIs(
    applno,
    submissionType,
    submissionNumber,
    apiIndications,
    center,
    documentId,
    sourceSubmission
  );

  useEffect(() => {
    (async () => {
      try {
        const authUser = await Auth.currentUserInfo();
        setUser({
          curator_name: authUser?.attributes?.['custom:user'] ?? '',
          curator_email: authUser?.attributes?.email ?? ''
        });
      } catch {
        setUser(null);
      }
    })();
  }, []);

  useEffect(() => {
    const getData = async () => {
      try {
        const response = await getIndications(
          applno,
          submissionType,
          submissionNumber,
          center,
          documentId
        );
        if (response.data.Status === HttpResponse.OK) {
          let {
            indications,
            // eslint-disable-next-line prefer-const
            previous_submission: dataFromPrevSubmission,
            // eslint-disable-next-line prefer-const
            previous_application: previousApplication = null
          } = response.data.Success;
          const readonlySourceSubmission = response.data.Success?.source_submission ?? null;
          setSourceSubmission(readonlySourceSubmission);

          indications = indications.map((ind: any) => ({
            ...ind,
            dataFromPrevSubmission,
            expanded: !!dataFromPrevSubmission
          }));

          adminDispatch({
            type: AdminActions.SET_SUBMISSION_INDICATIONS,
            value: indications
          });

          if (dataFromPrevSubmission && previousApplication) {
            setPreviousSubmission(
              `${previousApplication.applno}-${previousApplication.submission_type}-${previousApplication.submission_number}`
            );
            setUsePrevSubmissionData(true);
          } else {
            setPreviousSubmission(null);
            setUsePrevSubmissionData(false);
          }
        } else if (response.data.Status === HttpResponse.NotFound) {
          setSourceSubmission(null);
          adminDispatch({
            type: AdminActions.SET_SUBMISSION_INDICATIONS,
            value: []
          });
        } else {
          setSourceSubmission(null);
          await dispatch({
            type: Actions.SET_ALERT,
            value: {
              status: true,
              message: 'Could not find indications for the given submission details'
            }
          });
        }
      } catch (e: any) {
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: e.message }
        });
      }
    };
    getData();
    getLabelPdf(applno, submissionType, submissionNumber, center, documentId);
  }, [
    applno,
    submissionType,
    submissionNumber,
    center,
    documentId,
    dispatch,
    adminDispatch,
    getLabelPdf
  ]);

  return (
    <Box sx={styles.root}>
      <Box pb={2}>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Stack direction='row' alignItems='center' gap={1}>
            <KeyboardBackspaceIcon sx={styles.backIcon} />
            <Typography
              onClick={() => history.goBack()}
              variant='h5'
              display='inline-block'
              sx={styles.goBackTypography}>
              Go Back
            </Typography>
          </Stack>

          <Stack direction='row' spacing={2}>
            <Button variant='contained' color='primary' onClick={comparePdfs.openComparePdfs}>
              Compare PDFs
            </Button>
            <Button variant='contained' color='primary' onClick={compareVersions.openComparison}>
              Compare Versions
            </Button>
          </Stack>
        </Stack>
      </Box>
      <Stack direction='row' sx={styles.mainStack}>
        <Box flex={1} overflow='auto'>
          <SBASForm
            applno={applno}
            submission_type={submissionType}
            submission_number={submissionNumber}
            center={center}
            documentId={documentId}
          />
          <Box sx={styles.contentCard}>
            <Typography variant='h6' color='black'>
              Indications for {appKey}
            </Typography>
            {sourceSubmission && (
              <Alert severity='warning' sx={styles.readOnlyAlert}>
                <Typography variant='body2'>
                  Indication data for this submission was copied from{' '}
                  {sourceSubmissionUrl ? (
                    <Link href={sourceSubmissionUrl} target='_blank' rel='noopener noreferrer'>
                      {sourceSubmissionKey}
                    </Link>
                  ) : (
                    sourceSubmissionKey
                  )}{' '}
                  ({sourceSubmission.data_source?.toUpperCase() ?? 'Source'}, document IDs:{' '}
                  {sourceDocumentIds}). Existing copied indication and study edits must be made on{' '}
                  {sourceSubmissionUrl ? (
                    <Link href={sourceSubmissionUrl} target='_blank' rel='noopener noreferrer'>
                      the source submission
                    </Link>
                  ) : (
                    'the source submission'
                  )}{' '}
                  or by requesting change detection updates from the{' '}
                  <Link href={changeDashboardUrl} target='_blank' rel='noopener noreferrer'>
                    change dashboard
                  </Link>
                  .
                </Typography>
              </Alert>
            )}

            <Box>
              {previousSubmission && (
                <Box pt={1}>
                  <Typography variant='h6' color='black'>
                    We found indications for previous submission - {previousSubmission}
                  </Typography>
                  <Box display='flex' alignItems='center'>
                    <Typography>Use Previous Submission Data as default value</Typography>
                    <Switch
                      checked={usePrevSubmissionData}
                      onChange={() => setUsePrevSubmissionData(prev => !prev)}
                    />
                  </Box>
                </Box>
              )}
            </Box>
            <IndicationForm
              apiIndications={apiIndications}
              applno={applno}
              submission_type={submissionType}
              submission_number={submissionNumber}
              center={center}
              documentId={documentId}
              indicationApi={indicationApi}
              readOnly={hasSourceSubmission}
            />
          </Box>
        </Box>
        <Box flex={1}>
          {(loading || errMessage) && (
            <Box sx={styles.pdfLoadingWrapper}>
              <Box sx={styles.pdfLoadingCenter}>
                {loading ? (
                  <CircularProgress />
                ) : (
                  <Typography variant='h5'>{errMessage}</Typography>
                )}
              </Box>
            </Box>
          )}
          <iframe src={pdfUrl} frameBorder='0' title='Label' width='100%' height='100%' />
        </Box>
      </Stack>

      <CompareVersionsModal
        open={compareVersions.open}
        onClose={compareVersions.closeComparison}
        isLoadingPrevData={compareVersions.isLoadingPrevData}
        indicationPairs={compareVersions.indicationPairs}
        onCopyIndication={prevItem => compareVersions.setPendingCopyIndication(prevItem)}
        onCopyStudy={compareVersions.handleCopyStudy}
        isCopying={compareVersions.isCopying}
        copyingStudyId={compareVersions.copyingStudyId}
        pendingCopyIndication={compareVersions.pendingCopyIndication}
        onConfirmCopyIndication={async () => {
          if (compareVersions.pendingCopyIndication != null) {
            await compareVersions.handleCopyIndication(compareVersions.pendingCopyIndication);
          }
          compareVersions.setPendingCopyIndication(null);
        }}
        onCancelCopyIndication={() => compareVersions.setPendingCopyIndication(null)}
        indicationApi={indicationApi}
        onRefreshAfterSave={refreshCurrentIndications}
        onShowSnackbar={onShowSnackbar}
        nctIdAttributeId={compareVersions.nctIdAttributeId}
        identifierAttributeId={compareVersions.identifierAttributeId}
        attributeOptions={compareVersions.attributesList}
        readOnly={hasSourceSubmission}
      />

      <ComparePdfsModal
        open={comparePdfs.open}
        onClose={comparePdfs.closeComparePdfs}
        previousApplication={comparePdfs.previousApplication}
        previousPdfUrl={comparePdfs.previousPdfUrl}
        previousPdfLoading={comparePdfs.previousPdfLoading}
        previousPdfError={comparePdfs.previousPdfError}
        isLoadingPdfModal={comparePdfs.isLoadingPdfModal}
        currentPdfUrl={comparePdfs.currentComparePdfUrl}
        currentPdfLoading={comparePdfs.currentComparePdfLoading}
        currentPdfError={comparePdfs.currentComparePdfError}
        appKey={appKey}
        pdfViewMode={pdfViewMode}
        onPdfViewModeChange={mode => {
          setPdfViewMode(mode);
          const sectionParam = mode === 'full_label' ? undefined : mode;
          comparePdfs.loadPdfsForTab(sectionParam, documentId);
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        message={snackbar.message}
        sx={snackbar.isError ? styles.snackbarError : styles.snackbarSuccess}
      />
    </Box>
  );
};

export default SnapshotSummary;
